
// src/api/axiosInstance.ts
import axios from "axios";
import { toast } from "react-toastify";

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "/api",
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Enable sending cookies
  timeout: 480000, // 480 second timeout
});

// Add a request interceptor to fix baseURL issues and add Bearer token only for verification
axiosInstance.interceptors.request.use(
  (config) => {
    // If baseURL already ends with '/api' and the request url starts with '/api',
    // strip the leading '/api' so we don't get '/api/api/...'
    if (config.baseURL?.endsWith("/api") && config.url?.startsWith("/api/")) {
      config.url = config.url.replace(/^\/api/, "");
    }

    // Only add Bearer token for the token verification endpoint
    // All other endpoints will use the JWT cookie (set via withCredentials: true)
    if (config.url?.includes("/verify-access-token")) {
      const token = localStorage.getItem("accessToken");
      if (token && !config.headers.Authorization) {
        console.log("[axiosInstance] Adding accessToken to /verify-access-token request");
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// helper to call refresh endpoint
async function attemptTokenRefresh() {
  try {
    await axiosInstance.post("/api/auth/refresh");
    return true;
  } catch {
    return false;
  }
}

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // if 401, not on auth paths, and we haven't retried yet:
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/api/auth/login") &&
      !originalRequest.url?.includes("/api/auth/signup") &&
      !originalRequest.url?.includes("/api/auth/refresh")
    ) {
      originalRequest._retry = true;
      const refreshed = await attemptTokenRefresh();
      if (refreshed) {
        // after refresh succeed, retry original
        return axiosInstance(originalRequest);
      }
      // refresh failed → fall through to logout logic
    }

    // Check if it's a true network error (no response) or timeout, not an intentional 5xx response
    const isNetworkError = !error.response || error.code === "ECONNABORTED";

    if (isNetworkError && !originalRequest._hasRetried) {
      originalRequest._hasRetried = true;

      // Wait 2 seconds and retry once
      await new Promise((resolve) => setTimeout(resolve, 2000));

      try {
        return await axiosInstance(originalRequest);
      } catch (retryError) {
        // If retry fails, show network error message
        toast.error("Please check your internet connection and try again.", {
          position: "top-center",
          autoClose: 5000,
        });
        return Promise.reject(retryError);
      }
    }

    // Don't redirect for authentication endpoints (login, signup, verification, me)
    const isAuthEndpoint =
      error.config?.url?.includes("/api/auth/login") ||
      error.config?.url?.includes("/api/auth/signup") ||
      error.config?.url?.includes("/api/auth/verify-otp") ||
      error.config?.url?.includes("/api/auth/verify-email") ||
      error.config?.url?.includes("/api/auth/verify-access-token") ||
      error.config?.url?.includes("/api/auth/me") ||
      error.config?.url?.includes("/api/auth/reset-forgot-password") ||
      error.config?.url?.includes("/api/auth/forgot-password");

    // Only handle 401 redirects for non-auth endpoints
    if (error.response?.status === 401 && !isAuthEndpoint) {
      // Don't redirect if already on login or 2FA page
      if (
        !window.location.pathname.includes("login") &&
        !window.location.pathname.includes("2fa") &&
        !window.location.pathname.includes("sign-up") &&
        !window.location.pathname.includes("activate") &&
        !window.location.pathname.includes("reset-forgot-password") &&
        !window.location.pathname.includes("forgot-password") 
      ) {
        window.location.href = "/log-in";
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
